<template>
  <div class="w-full px-1 lg:px-0 border-t-2 border-gray-05">
    <div v-if="hasStandardAttributes" qaid="standard-attributes-container">
      <pdl-collapse align-icon="start" qaid="standard-attributes-collapse">
        <pdl-collapse-item
          v-for="attribute in product.variantAttributesDirectory"
          :key="attribute.code"
          :name="attribute.displayName"
          :qaid="`collapse-item-${attribute.code}`"
          @state-changed="updateStateOfCollapseItem($event, attribute.code)"
        >
          <template #title>
            <current-selection
              :id="attribute.code"
              :component-type="attribute.displayName"
              :component-name="getCurrentlySelected(attribute).displayName"
              :is-active="activeCollapseItems[attribute.code]"
            />
          </template>
          <pdl-callout
            v-if="attribute.code.includes('sizeFrame') && isCustomizationMode"
            qaid="customization-size-callout"
          >
            <template #content>
              {{ $t('product.fdp.customizable.size.message') }}
            </template>
          </pdl-callout>
          <component
            :is="getAttributeComponent(attribute)"
            v-else
            :code="attribute.code"
            :product="product"
            :selected-variant="selectedVariant"
            :init-attribute-selection="initAttributeSelection"
            :stock-message="stockMessage"
            :attribute="getAttributeOptions(attribute)"
            :selected-attributes="selectedAttributes"
            :update-selected-attributes="updateSelectedAttributes"
            class="mb-2"
            @color-changed="
              (color) => {
                $emit('color-changed', color);
              }
            "
          >
          </component>
        </pdl-collapse-item>
      </pdl-collapse>
    </div>
    <div v-show="isCustomizationMode" class="mb-3">
      <custom-components-container v-if="hasCustomComponentOptionsLoaded" />
      <trek-skeleton v-else height="20rem" />
    </div>
  </div>
</template>

<script>
import {mapState, mapMutations, mapGetters} from 'vuex';
import {PdlCollapse, PdlCollapseItem} from '@pedal/pdl-collapse';
import {PdlCallout} from '@pedal/pdl-callout';
import CurrentSelection from '@/components/containers/buy-zone/CurrentSelection.vue';
import TrekSkeleton from '@/components/TrekSkeleton.vue';
import CustomComponentsContainer from '@/components/containers/buy-zone/customization/CustomComponentsContainer.vue';
import AttributeColor from '@/components/containers/buy-zone/attributes/AttributeColor.vue';
import AttributeDropdown from '@/components/containers/buy-zone/attributes/AttributeDropdown.vue';
import ProductAttributeButtonGrid from '@/components/containers/buy-zone/attributes/ProductAttributeButtonGrid.vue';

const AttributeComponents = {
  dropdown: AttributeDropdown,
  swatch: AttributeColor,
  radio: ProductAttributeButtonGrid,
};

const AttributeOptions = Object.freeze({
  COLOR: 'bike_colorSwatch',
  SIZE: 'bike_sizeFrame',
});

export default {
  name: 'ProductAttributesContainer',
  components: {
    PdlCollapse,
    PdlCollapseItem,
    CurrentSelection,
    TrekSkeleton,
    CustomComponentsContainer,
    PdlCallout,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
    initAttributeSelection: {
      type: Object,
      default: () => null,
    },
    value: {
      type: String,
      default: '',
    },
    stockMessage: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      selectedAttributes: {},
      selectedVariant: {},
      productDetailsSwatchSelector: 'swatches-swatch',
      activeCollapseItems: {},
    };
  },

  computed: {
    ...mapState('productDetails', ['selectedColor', 'isCustomizationMode']),
    ...mapState('familyDetails', ['models']),
    ...mapState('bikeCustomization', ['componentOptions', 'bikeData']),
    ...mapGetters('bikeCustomization', ['selections']),

    hasStandardAttributes() {
      return this.product?.variantAttributesDirectory?.length > 0;
    },
    hasCustomComponentOptionsLoaded() {
      return this.componentOptions?.length > 0;
    },
  },
  watch: {
    initAttributeSelection() {
      this.updateSelectedAttributes(this.initAttributeSelection);
    },
    selectedColor(newValue, oldValue) {
      if (oldValue && newValue !== oldValue) {
        this.$nextTick(() => {
          const selectedSwatch = document.querySelector(`#${this.productDetailsSwatchSelector}-${newValue}`);
          selectedSwatch?.click();
        });
      }
    },
  },
  mounted() {
    this.updateSelectedAttributes(this.initAttributeSelection);
  },
  methods: {
    ...mapMutations('savedLists', ['setBuyingZoneEntries']),

    getAttributeComponent(attribute) {
      return AttributeComponents?.[attribute?.type] ?? AttributeComponents.dropdown;
    },
    getAttributeOptions(attribute) {
      return attribute.options.map((option) => {
        return {
          ...attribute,
          ...option,
        };
      });
    },
    getSelectedVariant() {
      const variant = this.product.variants.find((v) => {
        return Object.entries(this.selectedAttributes).every(([key, value]) => {
          return v.variantAttributes[key] === value;
        });
      });

      return variant;
    },
    updateSelectedAttributes(selectedAttributes) {
      let validatedAttributes = {};

      Object.keys(selectedAttributes).forEach((code) => {
        const hasValidAttributeCode = this.product.variantAttributesDirectory.find(
          (attribute) => attribute.code === code
        );

        if (hasValidAttributeCode) {
          validatedAttributes[code] = selectedAttributes[code];
        }
      });

      this.selectedAttributes = {
        ...this.selectedAttributes,
        ...validatedAttributes,
      };

      const variant = this.getSelectedVariant();
      if (variant?.code) {
        let attribute = Object.keys(selectedAttributes)[0];
        this.$emit('attribute-selected', selectedAttributes[attribute]);
        this.$emit('update:modelValue', variant.code);
        this.setBuyingZoneEntries([{sku: variant.code, qty: 1}]);
      } else {
        this.$emit('update:modelValue', null);
      }

      this.selectedVariant = variant;
    },
    getCurrentlySelected(attribute) {
      let currentlySelected = {displayName: '', price: ''};
      if (attribute.code === AttributeOptions.COLOR) {
        if (!this.isCustomizationMode) {
          currentlySelected.displayName = attribute.options?.find(
            (option) => this.selectedColor === option.colorSwatchName
          )?.displayName;
        } else {
          const schemeName = this.bikeData?.themeName;
          currentlySelected.displayName = schemeName;
        }
      } else if (attribute.code === AttributeOptions.SIZE) {
        currentlySelected.displayName = attribute.options?.find(
          (option) => this.selectedAttributes[AttributeOptions.SIZE] === option.value
        )?.displayName;
      } else {
        const currentSelectionId = this.selections?.find((selection) => selection.groupId === attribute.id)?.choiceId;
        const currentChoice = attribute.choices?.find((choice) => choice.id === currentSelectionId);
        currentlySelected.displayName = currentChoice?.description;
        currentlySelected.price = currentChoice?.priceDifferenceAdvertised;
      }
      return currentlySelected;
    },
    updateStateOfCollapseItem(event, attributeCode) {
      this.activeCollapseItems[attributeCode] = event;
    },
  },
};
</script>
